import { withSpan } from './ast.js';
import { FormulaErrorCodes, FormulaParseError, formulaError } from './errors.js';
import { tokenize, tokenSpan } from './lexer.js';
import { cover } from './span.js';
/**
 * How deep a text may nest when its profile names no depth limit (§4.8, §8): enough for any formula a person
 * writes, far below what the recursive descent's stack holds in either implementation.
 */
export const DEFAULT_MAX_NESTING = 128;
/**
 * Recursive descent over the grammar of §4.2, one method per production. Spans are into the source text; a
 * parenthesized expression widens its inner node's span to include the parentheses, so an error on
 * `(A + B) * C` underlines the whole product.
 *
 * The descent is bounded (§8): every construct it recurses into — a parenthesis, a call's arguments, a branch of
 * `?:`, a unary operand — is one level of nesting, and a level beyond `maxNesting` is FL-E09 at its token. A
 * parenthesis adds no node to the tree, so the profile's depth limit alone would never see it; without this bound
 * a text of a few hundred parentheses exhausted the stack.
 *
 * Throws FormulaParseError on the first error.
 */
export function parse(text, maxNesting = DEFAULT_MAX_NESTING) {
    const parser = new Parser(tokenize(text), maxNesting);
    const root = parser.parseConditional();
    if (parser.current.kind !== 'end')
        throw parser.unexpected();
    return root;
}
class Parser {
    constructor(tokens, maxNesting) {
        this.tokens = tokens;
        this.maxNesting = maxNesting;
        this.index = 0;
        this.nesting = 0;
        this.parseOr = () => this.parseBinaryChain(this.parseAnd, ['||']);
        this.parseAnd = () => this.parseBinaryChain(this.parseEquality, ['&&']);
        this.parseEquality = () => this.parseBinaryChain(this.parseRelational, ['==', '!=']);
        this.parseRelational = () => this.parseBinaryChain(this.parseAdditive, ['<', '<=', '>', '>=']);
        this.parseAdditive = () => this.parseBinaryChain(this.parseMultiplicative, ['+', '-']);
        this.parseMultiplicative = () => this.parseBinaryChain(this.parseUnary, ['*', '/']);
        /** unary := ( '-' | '!' ) unary | primary */
        this.parseUnary = () => {
            if (!this.isPunctuation('-') && !this.isPunctuation('!'))
                return this.parsePrimary();
            const op = this.advance();
            const operand = this.nested(op, () => this.parseUnary());
            return { kind: 'unary', op: op.text, operand, span: [op.start, operand.span[1]] };
        };
    }
    /** Parses one level deeper, opened by `at`; FL-E09 at `at` beyond the limit. */
    nested(at, parse) {
        if (this.nesting >= this.maxNesting) {
            throw new FormulaParseError(formulaError(FormulaErrorCodes.LimitExceeded, tokenSpan(at), `The formula nests more than ${this.maxNesting} levels deep; the profile allows at most ${this.maxNesting}.`, {
                limit: 'nesting',
                max: String(this.maxNesting),
            }));
        }
        this.nesting++;
        const result = parse();
        this.nesting--;
        return result;
    }
    get current() {
        return this.tokens[this.index];
    }
    peek(offset) {
        return this.tokens[Math.min(this.index + offset, this.tokens.length - 1)];
    }
    isPunctuation(text) {
        return this.current.kind === 'punctuation' && this.current.text === text;
    }
    advance() {
        return this.tokens[this.index++];
    }
    /** conditional := coalesce ( '?' conditional ':' conditional )? */
    parseConditional() {
        const test = this.parseCoalesce();
        if (!this.isPunctuation('?'))
            return test;
        const question = this.advance();
        const then = this.nested(question, () => this.parseConditional());
        const colon = this.expect(':');
        const otherwise = this.nested(colon, () => this.parseConditional());
        return { kind: 'cond', test, then, else: otherwise, span: cover(test.span, otherwise.span) };
    }
    /** coalesce := or ( '??' or )* */
    parseCoalesce() {
        let left = this.parseOr();
        while (this.isPunctuation('??')) {
            this.advance();
            const right = this.parseOr();
            left = { kind: 'coalesce', left, right, span: cover(left.span, right.span) };
        }
        return left;
    }
    /** A left-associative chain of one precedence level: operand ( op operand )*. */
    parseBinaryChain(operand, operators) {
        let left = operand();
        while (this.current.kind === 'punctuation' && operators.includes(this.current.text)) {
            const op = this.advance().text;
            const right = operand();
            left = { kind: 'binary', op, left, right, span: cover(left.span, right.span) };
        }
        return left;
    }
    /** primary := number | string | 'true' | 'false' | 'null' | path | call | '(' formula ')' */
    parsePrimary() {
        const token = this.current;
        switch (token.kind) {
            case 'number':
                this.advance();
                return { kind: 'num', value: token.text, span: tokenSpan(token) };
            case 'string':
                this.advance();
                return { kind: 'str', value: token.text, span: tokenSpan(token) };
            case 'identifier':
                return this.parseIdentifier();
            case 'punctuation':
                if (token.text === '(')
                    return this.parseParenthesized();
                throw this.error(tokenSpan(token), `Expected an operand, but found '${token.text}'.`);
            default:
                throw this.error(tokenSpan(token), 'Expected an operand, but the formula ends here.');
        }
    }
    parseIdentifier() {
        const first = this.advance();
        if (first.text === 'true' || first.text === 'false')
            return { kind: 'bool', value: first.text === 'true', span: tokenSpan(first) };
        if (first.text === 'null')
            return { kind: 'null', span: tokenSpan(first) };
        if (this.isPunctuation('('))
            return this.parseCall(first);
        const path = [first.text];
        let end = first.end;
        while (this.isPunctuation('.')) {
            this.advance();
            const segment = this.expectKind('identifier', "an identifier after '.'");
            path.push(segment.text);
            end = segment.end;
        }
        return { kind: 'ref', symbol: null, path, span: [first.start, end] };
    }
    /** call := identifier '(' ( argument ( ',' argument )* )? ')' */
    parseCall(fn) {
        const open = this.advance();
        const args = [];
        const named = [];
        this.nested(open, () => this.parseArguments(args, named));
        const close = this.expect(')');
        return { kind: 'call', fn: fn.text, fnSpan: tokenSpan(fn), args, named, span: [fn.start, close.end] };
    }
    parseArguments(args, named) {
        if (this.isPunctuation(')'))
            return;
        this.parseArgument(args, named);
        while (this.isPunctuation(',')) {
            this.advance();
            this.parseArgument(args, named);
        }
    }
    /** argument := formula | identifier ':' formula */
    parseArgument(args, named) {
        const next = this.peek(1);
        if (this.current.kind === 'identifier' && next.kind === 'punctuation' && next.text === ':') {
            const name = this.advance().text;
            this.advance();
            named.push({ name, value: this.parseConditional() });
            return;
        }
        args.push(this.parseConditional());
    }
    parseParenthesized() {
        const open = this.advance();
        const inner = this.nested(open, () => this.parseConditional());
        const close = this.expect(')');
        return withSpan(inner, [open.start, close.end]);
    }
    expect(punctuation) {
        if (!this.isPunctuation(punctuation)) {
            throw this.error(tokenSpan(this.current), this.current.kind === 'end'
                ? `Expected '${punctuation}', but the formula ends here.`
                : `Expected '${punctuation}', but found '${this.current.text}'.`);
        }
        return this.advance();
    }
    expectKind(kind, description) {
        if (this.current.kind !== kind) {
            throw this.error(tokenSpan(this.current), `Expected ${description}, but found '${this.current.text}'.`);
        }
        return this.advance();
    }
    unexpected() {
        return this.error(tokenSpan(this.current), `Unexpected '${this.current.text}'.`);
    }
    error(span, message) {
        return new FormulaParseError(formulaError(FormulaErrorCodes.ParseError, span, message));
    }
}
